import { useSelector, useDispatch } from 'react-redux'
import { Avatar, Box, Button, IconButton, Typography } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { addDetailsDriver } from '../../store/slices/timesheet/timesheetPageDetailSlice'
import dayjs from 'dayjs'
import 'dayjs/locale/pt'
import { pdf } from '@react-pdf/renderer'
import { usePrintReportData, PrintDocument } from './details/v2/Print/Print'
import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import CustomButton from '../v2/general/CustomButton/CustomButton'
import { setPdfClicked, setShowReport } from '../../store/slices/function/pdf'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { TimesheetStoreState } from '../../store/timesheetStore'

function HeaderApprovalDetails(): React.JSX.Element {
	const dispatch = useDispatch()
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)

	const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
	const navigate = useNavigate()
	const location = useLocation()
	const params = useParams()
	const [searchParams] = useSearchParams()

	// Detectar se está na rota de print
	const isTimesheetPrintRoute = location.pathname.endsWith('/print/')

	// Detect if it is in the print route of the service (URL contains ?Service)
	const isServicePrintRoute = location.pathname.includes('/print-service/')

	// Colect data from Redux to generate the timesheet PDF
	const pdfData = usePrintReportData()

	// Sincronizar estado Redux com a URL quando a página carregar ou URL mudar
	useEffect(() => {
		if (isTimesheetPrintRoute) {
			dispatch(setPdfClicked(true))
			dispatch(setShowReport(false))
		} else {
			dispatch(setPdfClicked(false))
			dispatch(setShowReport(false))
		}
	}, [isTimesheetPrintRoute, dispatch])

	const handlePDF = async () => {
		if (!isTimesheetPrintRoute) {
			// Primeiro clique: navegar para visualização de impressão
			if (params.id) {
				navigate(`/timesheet/${params.id}/print`)
			}
		} else {
			// Segundo clique: gerar PDF usando @react-pdf/renderer
			setIsGeneratingPDF(true)
			try {
				// Criamos o documento com os dados já coletados do Redux
				const blob = await pdf(
					<PrintDocument
						timesheetPage={pdfData.timesheetPage}
						servicesOriginal={pdfData.servicesOriginal}
						servicesExecution={pdfData.servicesExecution}
						servicesApproval={pdfData.servicesApproval}
						isTimesheetApproved={pdfData.isTimesheetApproved}
					/>,
				).toBlob()
				const url = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url

				// Gerar nome do arquivo no formato: folha-de-ponto-[nome motorista sem espaços]-[empresa]-[data folha de ponto Dia-mes-ano]-[timestamp da data do dia da impressão]
				const motoristaNome = (pdfData.timesheetPage?.driver?.driverName || 'motorista').replace(/\s+/g, '-')
				const empresa = (pdfData.timesheetPage?.driver?.companyName || 'empresa').replace(/\s+/g, '-')
				const dataFolhaPonto = dayjs(pdfData.timesheetPage?.timeSheetDate).format('DD-MM-YYYY')
				const timestamp = dayjs().format('YYYYMMDDHHmmss')

				link.download = `folha-de-ponto-${motoristaNome}-${empresa}-${dataFolhaPonto}-${timestamp}.pdf`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(url)
			} catch (error) {
				console.error('Erro ao gerar PDF:', error)
			} finally {
				setIsGeneratingPDF(false)
			}
		}
	}

	const handleBack = () => {
		navigate(-1)
	}

	const handleNewTimeSheet = () => {
		navigate(`/timesheet?${searchParams.toString()}`)
		dispatch(
			addDetailsDriver({
				driverTableId: 0,
				driver: {
					driverName: '',
					timesheetDate: '',
					totalServices: 0,
					timeSheetId: null,
					status: '',
					comments: '',
				},
			}),
		)
	}

	// Sticky state detection
	const [isSticky, setIsSticky] = useState(false)
	useEffect(() => {
		const targetScrollElement = isServicePrintRoute
			? null // Não há elemento pdf-content quando usando react-pdf
			: document.getElementById('pdf-content')
		const handleScroll = () => {
			if (targetScrollElement && targetScrollElement.scrollTop > 0) {
				setIsSticky(true)
			} else {
				setIsSticky(false)
			}
		}
		targetScrollElement?.addEventListener('scroll', handleScroll)
		return () => {
			targetScrollElement?.removeEventListener('scroll', handleScroll)
		}
	}, [isSticky, isServicePrintRoute])

	if (!timesheetPage) return <></>

	return (
		<Box
			sx={{
				marginBottom: isSticky ? 1 : 0,
				position: 'relative',
				width: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				overflow: 'visible',
				borderRadius: '5px',
				backgroundColor: isSticky ? 'var(--secondary-light)' : 'transparent',
				filter: isSticky
					? 'drop-shadow(0 3px 5px rgba(9, 32, 86, 0.20)) drop-shadow(0 6px 10px rgba(9, 32, 86, 0.14)) drop-shadow(0 1px 18px rgba(9, 32, 86, 0.12))'
					: 'none',
				transition: 'all 0.5s ease',
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<Button
					aria-label='ver ficha do motorista'
					sx={{ padding: 1 }}
					onClick={() => alert('Funcionalidade de ver ficha do motorista em desenvolvimento')}
				>
					<Avatar
						alt={`${timesheetPage?.driver.driverName} picture`}
						sizes={isSticky ? '24px' : '40px'}
						sx={{
							width: isSticky ? 24 : 40,
							height: isSticky ? 24 : 40,
							fontSize: isSticky ? '0.875rem' : '1.25rem',
							transition: 'all 0.5s ease',
							borderRadius: '100%',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: 'lightgrey 50% / cover no-repeat',
							fontWeight: 400,
							lineHeight: 1,
						}}
					/>
				</Button>
				<Box>
					<Typography
						variant={isSticky ? 'subtitle2' : 'subtitle1'}
						sx={{
							fontFamily: 'Poppins, sans-serif',
							fontSize: isSticky ? '0.875rem' : '1rem',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							transition: 'all 0.5s ease',
							color: 'var(--primary-dark)',
						}}
					>
						{timesheetPage?.driver.driverName}
					</Typography>
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							gap: 1,
						}}
					>
						<Typography
							variant='body2'
							sx={{
								fontFamily: 'Poppins, sans-serif',
								fontSize: '0.75rem',
								fontWeight: 400,
								lineHeight: 1.3,
								transition: 'all 0.5s ease',
								color: 'var(--primary-dark)',
							}}
						>
							Motorista nr {`${timesheetPage?.driver.internalNumber}`}
						</Typography>
						<Box
							sx={{
								width: '1px',
								height: '15px',
								backgroundColor: 'rgba(134, 149, 175, 0.15)',
							}}
						/>
						{timesheetPage?.driver?.companyName && (
							<Typography
								variant='body2'
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontSize: '0.75rem',
									fontWeight: 400,
									lineHeight: 1.3,
									transition: 'all 0.5s ease',
									color: 'var(--primary-dark)',
								}}
							>
								{timesheetPage?.driver?.companyName}
							</Typography>
						)}
					</Box>
				</Box>
			</Box>

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-end',
							textAlign: 'right',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Typography
								variant={isSticky ? 'subtitle2' : 'subtitle1'}
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontSize: isSticky ? '0.875rem' : '1rem',
									fontWeight: 600,
									lineHeight: 1.75,
									letterSpacing: '0.15px',
									transition: 'all 0.5s ease',
									color: 'var(--primary-dark)',
								}}
							>
								{dayjs(timesheetPage?.timeSheetDate).locale('pt').format('ddd DD/MM/YYYY') ??
									dayjs(new Date()).locale('pt').format('ddd DD/MM/YYYY')}
							</Typography>
						</LocalizationProvider>
						{/* 
							<Typography>
								// TODO: Implementar de acordo com design quando timesheetPage.service[0].serviceExecution tiver timestamps
								{timesheetPage?.isSubmitted ? 'Submetida' : 'Por submeter'}
							</Typography> 
						*/}
					</Box>
					{location.pathname.includes('/print') && (
						<CustomButton
							variant='outlined'
							color='secondary'
							size={isSticky ? 'small' : 'medium'}
							tabIndex={-1}
							onClick={handleBack}
						>
							Voltar
						</CustomButton>
					)}
					{!location.pathname.includes('/print') && (
						<CustomButton
							variant='contained'
							color='secondary'
							size={isSticky ? 'small' : 'medium'}
							tabIndex={-1}
							onClick={handlePDF}
							disabled={isGeneratingPDF}
						>
							Imprimir | Exportar
						</CustomButton>
					)}
				</Box>
				<IconButton
					aria-label='notifications'
					size={isSticky ? 'small' : 'medium'}
					tabIndex={-1}
					onClick={() => alert('Funcionalidade de notificações em desenvolvimento')}
					sx={{ borderRadius: '0 !important' }}
				>
					<NotificationsIcon
						sx={{
							fontSize: '24px',
							color: 'var(--primary)',
						}}
					/>
				</IconButton>
			</Box>
			<IconButton
				aria-label='close timesheet'
				size='small'
				tabIndex={-1}
				onClick={handleNewTimeSheet}
				sx={{
					border: '2px solid rgba(134, 149, 175, 0.15)',
					padding: '4px',
					position: 'absolute',
					top: '-3rem',
					right: '0.75rem',
					zIndex: 100,
					color: 'var(--primary)',
				}}
			>
				<ClearIcon
					sx={{
						fontSize: '20px',
					}}
				/>
			</IconButton>
		</Box>
	)
}

export default HeaderApprovalDetails
